import { useMemo } from 'react'
import { useWatch, type Control } from 'react-hook-form'

import { checkFieldCondition } from '../lib/actionHelper'
import type { DynamicField } from '../types'

/**
 * Processes an array of DynamicField definitions against current form values and
 * returns the subset of fields that are visible with `visible` and `required`
 * flags resolved.
 *
 * Usage:
 *  const visibleFields = useDynamicFields(control, fields)
 *
 *  - `control` is the react-hook-form `control` instance
 *  - `fields` is an array of DynamicField coming from the backend
 *
 * Example:
 *  const fields = [{ param_key: 'email', required: true }, { param_key: 'role', visible_if: { is_admin: true } }]
 *  const visibleFields = useDynamicFields(control, fields)
 *
 * Returns:
 *  Array<DynamicField & { visible: boolean; required: boolean }>
 */
export function useDynamicFields<T extends {}>(control: Control<T>, fields?: Array<DynamicField>) {
  const values = useWatch({ control }) as Record<string, unknown>

  const processed = useMemo(
    () =>
      fields
        ?.map((field) => {
          const visibleIf = 'visible_if' in field ? field.visible_if : undefined
          const visible = !visibleIf || checkFieldCondition(values, visibleIf)

          const required =
            typeof field.required === 'boolean' ? field.required : checkFieldCondition(values, field.required || {})

          return { ...field, visible, required }
        })
        .filter((f) => f.visible),
    [fields, values],
  )

  return processed
}
